/**
 * Remediation sidebar badge utilities.
 *
 * Handoff / QA notes (DoD 7.1 / 7.6):
 * - The course sidebar badge count uses GET /api/remediation with meta.total (per_page=1),
 *   not Vuex-ORM row hydration, so pagination cannot under-count.
 * - Badge filters are built from buildCourseDashboardRemediationRawParams() — the same base
 *   params Remediations.vue uses — plus filter[passEligibleStatuses] (In Progress + Pending
 *   Instructor Grade, excluding course grades marked Remediation Passed or Remediated Grade).
 * - The badge number may differ from an unfiltered Remediations panel row count: the panel lists
 *   all non-graded remediations while the badge counts pass-eligible statuses only.
 * - The badge renders only for users with the global "view all remediation processes"
 *   permission (AppSidebarSummary.vue, CourseMenu.vue). Course instructors without that
 *   global permission do not see a badge.
 * - This module does not populate the Remediation Vuex-ORM entity for the badge. Smoke-test
 *   proctor dashboard navigation from a course page after merge.
 */

import RemediationProcess from '@/models/RemediationProcess.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export const REMEDIATION_PANEL_REFRESH_EVENT = 'remediation-sidebar-count-refresh';

export const PASS_ELIGIBLE_REMEDIATION_STATUSES = [
    RemediationProcess.IN_PROGRESS_STATUS,
    RemediationProcess.PENDING_INSTRUCTOR_GRADE_STATUS,
];

/**
 * Shared raw query params for course-scoped /api/remediation requests.
 * Used by Remediations.vue and extended by the sidebar badge fetch.
 */
export function buildCourseDashboardRemediationRawParams(courseId, { filterType = 'course-dashboard' } = {}) {
    const params = [{ filter_type: filterType }];

    if (courseId) {
        params.push({ 'filter[course_id]': courseId });
    }

    params.push({ 'filter[activeEnrollment]': true });

    return params;
}

function serializeRawParams(params) {
    const searchParams = new URLSearchParams();

    for (const entry of params) {
        for (const [key, value] of Object.entries(entry)) {
            searchParams.append(key, String(value));
        }
    }

    return searchParams.toString();
}

/**
 * Raw params for the sidebar badge count (meta.total, not paginated row hydration).
 */
export function buildRemediationSidebarBadgeRawParams(courseId) {
    return [
        ...buildCourseDashboardRemediationRawParams(courseId),
        { 'filter[passEligibleStatuses]': 1 },
        { per_page: 1 },
    ];
}

export function buildRemediationSidebarBadgeQueryUrl(courseId) {
    return `/api/remediation?${serializeRawParams(buildRemediationSidebarBadgeRawParams(courseId))}`;
}

/**
 * Query builder for the course Remediation page (course-grades table).
 */
export function buildCourseRemediationPageQuery(
    courseId,
    {
        page = 1,
        perPage = 25,
        passEligibleOnly = false,
        includeDetails = false,
        sortField = null,
        sortOrder = null,
    } = {}
) {
    const queryBuilder = new QueryBuilder('course-grades')
        .raw({ scopeToRemediation: true })
        .filter('course_id', courseId)
        .filter('isFailing', 1)
        .filter('withIsFailing', 1)
        .filter('isPublished', 1)
        .page(page)
        .pageSize(perPage);

    if (includeDetails) {
        queryBuilder
            .include('user')
            .include(['remediation', 'user', 'course', 'gradingScheme', 'gradingSchemeCriteria', 'statuses'])
            .append('percentage_value');
    }

    if (passEligibleOnly) {
        queryBuilder.filter('passEligibleRemediation', 1);
    }

    if (sortField) {
        queryBuilder.sort(sortField, sortOrder || 'asc');
    }

    return queryBuilder;
}

export function isPassEligibleRemediationStatus(remediationStatus) {
    return PASS_ELIGIBLE_REMEDIATION_STATUSES.includes(remediationStatus);
}

/** @deprecated Use isPassEligibleRemediationStatus */
export function isPendingInstructorGradeRemediation(remediation) {
    return remediation?.remediation_status === RemediationProcess.PENDING_INSTRUCTOR_GRADE_STATUS;
}

export function notifyRemediationPanelRefresh(courseId) {
    window.dispatchEvent(
        new CustomEvent(REMEDIATION_PANEL_REFRESH_EVENT, {
            detail: { courseId: Number(courseId) },
        })
    );
}

export function subscribeRemediationPanelRefresh(handler) {
    window.addEventListener(REMEDIATION_PANEL_REFRESH_EVENT, handler);

    return () => window.removeEventListener(REMEDIATION_PANEL_REFRESH_EVENT, handler);
}

/**
 * Pass-eligible remediation rows for the course (In Progress + Pending Instructor Grade),
 * scoped to active enrollments. Uses meta.total so pagination cannot cap the badge.
 */
export async function fetchPendingInstructorGradeCount(courseId) {
    if (!courseId) {
        return 0;
    }

    const { data } = await axios.get(buildRemediationSidebarBadgeQueryUrl(courseId));

    return data?.meta?.total ?? 0;
}

/** @deprecated Use fetchPendingInstructorGradeCount — kept for call-site compatibility. */
export async function fetchCourseRemediationsForSidebar(courseId) {
    return fetchPendingInstructorGradeCount(courseId);
}

/** @deprecated Use buildRemediationSidebarBadgeQueryUrl */
export function buildPendingInstructorGradeBadgeQueryUrl(courseId) {
    return buildRemediationSidebarBadgeQueryUrl(courseId);
}
